/**
 * WEB COPY of LetterRecordOfferScreen.tsx — picked by the web bundler ahead
 * of the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Recording is a phone thing for now: browsers produce a different audio
 * format than the phone, free-tier recordings have nowhere to live on the
 * web (no vault keys survive a visit), and the encryption path is built on
 * the phone's filesystem. So instead of the two record choices, this screen
 * tells the reader how to do it in the app — same header, same sheet, same
 * lock note. LetterRecorderScreen.web.tsx re-exports this screen so a direct
 * URL to the recorder lands here too.
 */
import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { View, StyleSheet, SafeAreaView, TouchableOpacity, Image } from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { ms, s } from '../../utils/responsive';

const STEPS = [
  'Open FinalKey on your phone.',
  'Go to Letters and pick this letter.',
  'Tap "Read it aloud for them".',
];

export const LetterRecordOfferScreen: React.FC = () => {
  const navigation = useNavigation<any>();

  const goBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('LettersHome');
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={goBack}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={require('../../assets/icons/back.png')} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>One more thing</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        <View style={styles.orionRow}>
          <Image source={require('../../assets/icons/chat.png')} style={styles.orionIcon} />
          <Text style={styles.orionText}>
            Reading it out loud changes it. Your voice is the part they can't
            reread on paper.
          </Text>
        </View>

        <View style={styles.choiceCard}>
          <View style={styles.choiceHead}>
            <Image
              source={require('../../assets/icons/communication.png')}
              style={styles.choiceIcon}
            />
            <View style={styles.choiceText}>
              <Text style={styles.choiceTitle}>Record on your phone</Text>
              <Text style={styles.choiceSub}>
                Recording happens in the FinalKey app, where the reading is
                encrypted and kept on your phone. About two minutes.
              </Text>
            </View>
          </View>
          {STEPS.map((step, index) => (
            <View key={step} style={styles.stepRow}>
              <View style={styles.stepMarker}>
                <Text style={styles.stepMarkerText}>{index + 1}</Text>
              </View>
              <Text style={styles.stepText}>{step}</Text>
            </View>
          ))}
        </View>

        <View style={styles.note}>
          <Image source={require('../../assets/icons/BlueLock.png')} style={styles.noteIcon} />
          <Text style={styles.noteText}>
            Recordings are encrypted like everything else, and stay yours to
            keep. Once you've made one, it shows up here.
          </Text>
        </View>

        <TouchableOpacity
          style={styles.backToLetter}
          onPress={goBack}
          accessibilityRole="button"
          accessibilityLabel="Back to my letter"
        >
          <Text style={styles.backToLetterText}>Back to my letter</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
    paddingBottom: ms(20),
  },
  orionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: ms(10),
    marginBottom: ms(18),
  },
  orionIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#29BFC0',
  },
  orionText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  choiceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(12),
  },
  choiceHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: ms(14),
    marginBottom: ms(14),
  },
  choiceIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#29BFC0',
  },
  choiceText: {
    flex: 1,
  },
  choiceTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(2),
  },
  choiceSub: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(10),
    paddingVertical: ms(5),
  },
  stepMarker: {
    width: s(24),
    height: s(24),
    borderRadius: s(12),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepMarkerText: {
    color: '#FFFFFF',
    fontSize: ms(12, 0.3),
    fontWeight: '700',
  },
  stepText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#1C2C4F',
  },
  note: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(8),
    paddingHorizontal: ms(4),
    marginBottom: ms(20),
  },
  noteIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  noteText: {
    flex: 1,
    fontSize: ms(12, 0.3),
    lineHeight: ms(17, 0.3),
    color: '#6B7280',
  },
  backToLetter: {
    alignItems: 'center',
    paddingVertical: ms(13),
    borderRadius: ms(25),
    backgroundColor: '#FF9F5A',
  },
  backToLetterText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
});

export default LetterRecordOfferScreen;
